<template>
  <v-card v-if="financials.length > 0">
    <v-card-title class="mb-5">
      <section-header icon="mdi mdi-chart-timeline-variant-shimmer">
        {{ $t('financials.company_financial_development') }}
      </section-header>
    </v-card-title>
    <v-card-text class="pb-0">
      <div id="container"></div>
    </v-card-text>
    <v-card-actions>
      <v-spacer v-if="$vuetify.breakpoint.xsOnly"></v-spacer>
      <basic-button :block="$vuetify.breakpoint.xsOnly"
                    :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
                    :outlined="$vuetify.breakpoint.xsOnly"
                    color="primary" text @click="goToFinancials">
        <v-icon v-if="$icons" left>mdi mdi-arrow-right</v-icon>
        {{ $t(`financials.check_financial_kpis`) }}
      </basic-button>
    </v-card-actions>
  </v-card>
</template>

<script>
import financials from "@/api/financials";
import tenantApi from "@/api/tenant";
import Highcharts from 'highcharts';
import highchartTranslations from "@/config/highchart";
import dayjs from "dayjs";
import SectionHeader from "@/components/SectionHeader";
import BasicButton from "@/components/BasicButton";

export default {
  name: "FinancialDevelopmentChart",
  components: {BasicButton, SectionHeader},
  data() {
    return {
      financials: [],
      fetchFinancialsDone: false,
      tenant: null,
      chart: null,
    }
  },
  methods: {
    goToFinancials() {
      this.$router.push({name: 'tenant_financials'});
    },
    async reloadFinancials() {
      try {
        this.financials = await financials.getFinancials();
        if (this.financials.length === 0) {
          this.tenant = await tenantApi.getCurrentTenant();
          if (this.tenant.dunsNumber && !this.fetchFinancialsDone) {
            await this.fetchFinancial();
          }
        }
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    async fetchFinancial() {
      try {
        await financials.fetchDnbData();
      } catch (e) {
        //this.$handleApiError(e); // DnB API key might be missing, do not show error
      }
      this.fetchFinancialsDone = true;
      await this.reloadFinancials();
    },
    createChart() {
      if (!this.financials || this.financials.length === 0) {
        return;
      }
      Highcharts.setOptions({lang: highchartTranslations[this.$userInfo.language]});
      const chart = Highcharts.chart('container', {
        chart: {
          type: 'column',
          backgroundColor: 'transparent',
          style: {
            fontFamily: 'Jost'
          },
        },
        title: {
          text: null,
        },
        accessibility: {
          description: ''
        },
        xAxis: {
          categories: this.data.map(d => d.year),
          crosshair: true,
        },
        yAxis: {
          title: {
            text: this.financials?.[0]?.currency || '',
          },
          plotLines: [
            {
              value: 0,
              width: 1,
              color: "#fff",
              zIndex: 10,
            }
          ],
          stackLabels: {
            enabled: true,
          }
        },
        tooltip: {
          headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
          pointFormat: `<tr><td style="color:{series.color};padding:0">{series.name}: </td>` +
            `<td style="padding:0"><b>{point.y} ${this.financials?.[0]?.currency || ''}</b></td></tr>`,
          footerFormat: '</table>',
          shared: true,
          useHTML: true
        },
        plotOptions: {
          column: {
            pointPadding: 0.1,
            borderWidth: 0
          },
        },
        series: [
          {
            name: this.$t('financials.values.sales_revenue'),
            data: this.data.map(d => d.salesRevenue),
            color: this.$vuetify.theme.themes.light.primary
          },
          {
            name: this.$t('financials.values.sales_margin'),
            data: this.data.map(d => d.salesMargin),
            color: this.$vuetify.theme.themes.light.secondary
          },
          {
            name: this.$t('financials.values.ebitda'),
            data: this.data.map(d => d.ebitda),
            color: this.$vuetify.theme.themes.light.accent
          }
        ],
        credits: false,
        /* responsive: {
          rules: [{
            condition: {
              maxWidth: 500
            },
            chartOptions: {
              pane: {
                size: '72%'
              }
            }
          }]
        }
        */
      });

      this.chart = chart;
    },
  },
  computed: {
    data() {
      return this.financials
        .map((f) => {
          return {
            year: dayjs(f.financialStatementToDate).year(),
            salesRevenue: f.salesRevenue,
            salesMargin: f.salesMargin,
            ebitda: f.ebitda
          }
        })
        .sort((a, b) => {
          return a.year - b.year;
        });
    },
  },
  async mounted() {
    await this.reloadFinancials();
    await this.createChart();
  }
}
</script>

<style scoped>

</style>
